import React, { useState, useEffect, useReducer, useContext, useCallback, useRef } from "react";
import openSocket from "socket.io-client";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SpyModeProvider } from "../../context/SpyMode/SpyModeContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { mergeContactPreservingManualName } from "../../helpers/contactIdentity";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
	pinnedSection: {
		paddingLeft: 16,
		paddingTop: 6,
		paddingBottom: 4,
		fontSize: "0.75rem",
		fontWeight: 600,
		color: "#f39c12",
		letterSpacing: "0.05em",
		textTransform: "uppercase",
		display: "flex",
		alignItems: "center",
		gap: 4,
	},
	pinnedItem: {
		cursor: "grab",
		"&:active": { cursor: "grabbing" },
	},
}));

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		} else {
			state.push(ticket);
		}

		return [...state];
	}

	// IS-2862: atualiza um ticket que JÁ está na lista, sem inserir e sem
	// reordenar. É o que a aba Buscar precisa: o UPDATE_TICKET acima insere
	// quando não encontra, e a busca não pode receber conversa que não casa com
	// o termo pesquisado. Mescla sobre o item atual para não perder campos que o
	// payload do socket não carrega.
	if (action.type === "UPDATE_TICKET_IN_PLACE") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex === -1) {
			return state;
		}

		const prevTicket = state[ticketIndex];
		const newState = [...state];
		newState[ticketIndex] = {
			...prevTicket,
			...ticket,
			contact: ticket.contact
				? mergeContactPreservingManualName(prevTicket.contact, ticket.contact)
				: prevTicket.contact
		};

		return newState;
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			const prevContact = state[ticketIndex].contact;
			state[ticketIndex] = {
				...ticket,
				contact: ticket.contact
					? mergeContactPreservingManualName(prevContact, ticket.contact)
					: prevContact
			};
			state.push(state.splice(ticketIndex, 1)[0]);
		} else {
			state.push(ticket);
		}
		state.sort(function(a,b){
			if (action.tipoOrdenacao === true) {
				// "Novo": mais recentes primeiro (decrescente)
				return new Date(b.lastInteractionDate == null ? b.updatedAt : b.lastInteractionDate) - new Date(a.lastInteractionDate == null ? a.updatedAt : a.lastInteractionDate);
			} else {
				return new Date(a.lastMessageDate == null ? a.updatedAt : a.lastMessageDate) - new Date(b.lastMessageDate == null ? b.updatedAt : b.lastMessageDate);
			}
		 });
	
		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = mergeContactPreservingManualName(
				state[ticketIndex].contact,
				contact
			);
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};


const TicketsList = (props) => {
	const { status, searchParam, showAll, selectedTagIds, selectedProductIds, selectedQueueIds, updateCount, style, filtros, tab, tagList, productList, withUnreadMessages } = props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const { user, tipoOrdenacao } = useContext(AuthContext);
	const [pinnedTickets, setPinnedTickets] = useState([]);
	const dragItem = useRef(null);
	const dragOverItem = useRef(null);

	const fetchPinned = useCallback(async () => {
		try {
			const { data } = await api.get("/tickets-pinned");
			setPinnedTickets(data);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		if (tab !== "search") {
			fetchPinned();
		}
	}, [tab, fetchPinned]);

	const handleDragStart = (index) => {
		dragItem.current = index;
	};

	const handleDragEnter = (index) => {
		dragOverItem.current = index;
		const newList = [...pinnedTickets];
		const draggedItem = newList.splice(dragItem.current, 1)[0];
		newList.splice(dragOverItem.current, 0, draggedItem);
		dragItem.current = dragOverItem.current;
		dragOverItem.current = null;
		setPinnedTickets(newList);
	};

	const handleDragEnd = async () => {
		const orderedIds = pinnedTickets.map(t => t.id);
		try {
			await api.put("/tickets-pinned/reorder", { orderedIds });
		} catch (err) {
			toastError(err);
		}
	};

	const pinnedIds = new Set(pinnedTickets.map(t => t.id));

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, searchParam, dispatch, showAll, selectedTagIds, selectedProductIds, selectedQueueIds, filtros]);

	const { tickets, hasMore, loading } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		filtros,
		tab,
		withUnreadMessages,
		queueIds: JSON.stringify(selectedQueueIds),
		tagIds: JSON.stringify(selectedTagIds),
		productIds: JSON.stringify(selectedProductIds),
	});

	useEffect(() => {
		//if (!status && !searchParam, !filtros) return;
		if(tab === "search"){
			dispatch({ type: "RESET" });
		}
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
		
	}, [tickets, status, searchParam, filtros]);

	useEffect(() => {
		const socket = openSocket(process.env.REACT_APP_BACKEND_URL);

		const shouldUpdateTicket = ticket => 
			(!ticket.userId || ticket.userId === user?.id || showAll) &&
			(!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1);

		const notBelongsToUserQueues = ticket =>
			ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

		const notBelongsToTags = ticket =>
			selectedTagIds.indexOf(ticket.tagId) === -1;

		const notBelongsToProducts = ticket =>
			selectedProductIds.indexOf(ticket.productId) === -1;

		socket.on("connect", () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		});

		socket.on("ticket", data => {
			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			// IS-2579: a lista de "não lidas" (withUnreadMessages) só deve conter
			// tickets com unreadMessages > 0. O filtro só vale na busca inicial; em
			// updates ao vivo o ticket era injetado sem revalidar (ex.: aviso de
			// linha desconectada com fromMe:false fazia o grupo já lido aparecer em
			// "não lidas" até o F5). Aqui removemos quando o ticket não tem não-lidas.
			if (data.action === "update" && withUnreadMessages && !(data.ticket.unreadMessages > 0)) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
				return;
			}

			if (data.action === "update" && shouldUpdateTicket(data.ticket) && tab !== "search" && (selectedTagIds.length == 0 || selectedTagIds.indexOf(data.ticket.tagId) > -1) && (selectedProductIds.length == 0 || selectedProductIds.indexOf(data.ticket.productId) > -1)) {
				dispatch({
					type: "UPDATE_TICKET",
					payload: data.ticket,
				});
				setPinnedTickets(prev => prev.map(t => t.id === data.ticket.id ? { ...data.ticket, pinnedOrder: t.pinnedOrder } : t));
			}

			// IS-2862: na aba Buscar o UPDATE_TICKET acima não roda, então trocar a
			// etiqueta/produto atualizava a conversa mas deixava o item da lista com o
			// valor antigo até o F5. Atualiza no lugar: só mexe no ticket que já está
			// no resultado, sem inserir conversa que não casa com a busca e sem
			// reordenar — a lista não recarrega e a conversa aberta continua onde está.
			if (data.action === "update" && tab === "search") {
				dispatch({
					type: "UPDATE_TICKET_IN_PLACE",
					payload: data.ticket,
				});
			}

			// IS-2862: na aba Buscar o resultado é justamente de outros usuários e
			// outras filas (ex.: supervisora filtrando por VENDEDOR), e `showAll` nem
			// é passado (undefined), então `!showAll` é sempre true. Sem o guard de
			// "search" um update ao vivo (trocar etiqueta/produto, que o backend emite
			// para a sala "notification" em UpdateTicketTagService) removia a conversa
			// da lista, e o UPDATE_TICKET acima não reinsere na busca — só voltava no
			// F5. Os DELETE abaixo valem só fora da aba Buscar.
			if (data.action === "update" && data.ticket.userId && data.ticket.userId !== user?.id && !showAll && tab !== "search") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "update" && notBelongsToUserQueues(data.ticket) && tab !== "search") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "update" && notBelongsToTags(data.ticket) && selectedTagIds.length != 0 && notBelongsToProducts(data.ticket) && selectedProductIds.length != 0 && tab !== "search") {

				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		});

		socket.on("appMessage", data => {
			// IS-2579: não injetar na lista de "não lidas" um ticket sem unreadMessages
			// (ex.: aviso de linha desconectada com fromMe:false após envio do operador).
			if (data.action === "create" && withUnreadMessages && !(data.ticket.unreadMessages > 0)) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
				return;
			}

			if (data.action === "create" && shouldUpdateTicket(data.ticket) && tab !== "search" && (selectedTagIds.length == 0 || selectedTagIds.indexOf(data.ticket.tagId) > -1) && (selectedProductIds.length == 0 || selectedProductIds.indexOf(data.ticket.productId) > -1)) {
				const ticketWithLastMessage = { ...data.ticket, lastMessage: data.message?.body ?? data.ticket.lastMessage };
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: ticketWithLastMessage,
					tipoOrdenacao: tipoOrdenacao
				});
				setPinnedTickets(prev => prev.map(t => t.id === data.ticket.id ? { ...ticketWithLastMessage, pinnedOrder: t.pinnedOrder } : t));
			}
		});

		socket.on("contact", data => {
			if (data.action === "update" && tab !== "search") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		});

		return () => {
			socket.disconnect();
		};
		// IS-2862: o handler lê `tab`, então ele entra nas deps. Hoje é inerte (o
		// TabPanel desmonta a lista ao trocar de aba, então `tab` é constante em cada
		// instância), mas evita closure defasado se o TabPanel passar a manter os
		// filhos montados.
	}, [status, showAll, user, selectedTagIds, selectedProductIds, selectedQueueIds, tipoOrdenacao, tab]);

	useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
	ticketsList.sort(function(a,b){
		if (tipoOrdenacao === true) {
			// "Novo": mais recentes primeiro (decrescente)
			return new Date(b.lastInteractionDate == null ? b.updatedAt : b.lastInteractionDate) - new Date(a.lastInteractionDate == null ? a.updatedAt : a.lastInteractionDate);
		} else {
			return new Date(a.lastMessageDate == null ? a.updatedAt : a.lastMessageDate) - new Date(b.lastMessageDate == null ? b.updatedAt : b.lastMessageDate);
		}
	 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  	}, [ticketsList]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{pinnedTickets.length > 0 && tab !== "search" && (
						<SpyModeProvider>
							<div className={classes.pinnedSection}>
								📌 Fixadas
							</div>
							{pinnedTickets.map((ticket, index) => (
								<div
									key={ticket.id}
									draggable
									onDragStart={() => handleDragStart(index)}
									onDragEnter={() => handleDragEnter(index)}
									onDragEnd={handleDragEnd}
									className={classes.pinnedItem}
								>
									<TicketListItem
										ticket={ticket}
										tagList={tagList}
										productList={productList}
										isPinned={true}
										onPinChange={fetchPinned}
									/>
								</div>
							))}
							<Divider />
						</SpyModeProvider>
					)}
					{ticketsList.filter(t => !pinnedIds.has(t.id)).length === 0 && !loading && pinnedTickets.length === 0 ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>

							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							<SpyModeProvider>
								{ticketsList.filter(t => !pinnedIds.has(t.id)).map(ticket => (
									<TicketListItem
										ticket={ticket}
										key={ticket.id}
										tagList={tagList}
										productList={productList}
										isPinned={false}
										onPinChange={fetchPinned}
									/>
								))}
							</SpyModeProvider>
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
